import { formatVehicleLabel } from "./formatVehicleLabel";
import { isPlaceholderRegoValue } from "./vehiclePlaceholders";

export const ROTATING_QR_PREFIX = "P1R.";
const SUBSCRIPTION_ID_RE = /^[a-f0-9]{24}$/i;

// Refetch this long before the code expires so the member never shows a dead code.
export const REDEEM_QR_REFRESH_LEAD_MS = 5000;
const MIN_REFETCH_DELAY_MS = 1000;

export const COFFEE_PASS_LIVE_QR_MESSAGE = "Ask the member to open their Coffee Pass and show the live QR.";
export const COFFEE_PASS_EXPIRED_QR_MESSAGE =
  "This code has expired. Ask the member to wait for the next code and scan again.";
export const COFFEE_PASS_OFFLINE_MESSAGE = "Connect to the internet to show your pass.";

export function isCoffeePassProduct(product) {
  return product?.vertical === "cafe";
}

export function isCoffeePassSubscription(subscription) {
  return isCoffeePassProduct(subscription?.product);
}

export function usesRotatingQr(subscription) {
  return subscription?.redemptionQrMode === "rotating";
}

export function isRotatingQrPayload(value) {
  return typeof value === "string" && value.trim().startsWith(ROTATING_QR_PREFIX);
}

/**
 * Scanner input routing. The live-QR prefix is checked before the 24-hex subscription id
 * and the rego fallback, because anything else used to be treated as a rego.
 * @returns {{ kind: "rotating" | "subscriptionId" | "rego" | "empty", value: string }}
 */
export function classifyScannerInput(raw) {
  const value = typeof raw === "string" ? raw.trim() : "";
  if (!value) return { kind: "empty", value };
  if (isRotatingQrPayload(value)) return { kind: "rotating", value };
  if (SUBSCRIPTION_ID_RE.test(value)) return { kind: "subscriptionId", value };
  return { kind: "rego", value };
}

/** Rego to show a member on their pass, or "" for Coffee Pass and placeholder vehicles. */
export function memberVehicleLabel(subscription) {
  if (isCoffeePassSubscription(subscription)) return "";
  const vehicle = subscription?.vehicle;
  if (!vehicle || isPlaceholderRegoValue(vehicle.rego)) return "";
  return formatVehicleLabel(vehicle);
}

/** Milliseconds until the wallet should fetch the next live code. */
export function nextRedeemQrFetchDelayMs(expiresAt, now = Date.now()) {
  const expiresMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expiresMs)) return MIN_REFETCH_DELAY_MS;
  return Math.max(MIN_REFETCH_DELAY_MS, expiresMs - now - REDEEM_QR_REFRESH_LEAD_MS);
}

export function secondsUntil(expiresAt, now = Date.now()) {
  const expiresMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expiresMs)) return 0;
  return Math.max(0, Math.ceil((expiresMs - now) / 1000));
}

/** Coffee-only members (every active, non-deleted membership is a Coffee Pass). */
export function isCoffeeOnlyMember(subscriptions) {
  const current = (Array.isArray(subscriptions) ? subscriptions : []).filter(
    (s) => s && s.deleted !== "yes"
  );
  return current.length > 0 && current.every(isCoffeePassSubscription);
}
